import React, { useState, useEffect, useRef } from 'react';

// --- Asset Configuration ---
const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

const COUNTRY_CODES = [
  { code: '+91', flag: '🇮🇳', name: 'India' },
  { code: '+1',  flag: '🇺🇸', name: 'USA' },
  { code: '+44', flag: '🇬🇧', name: 'UK' },
  { code: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: '+65', flag: '🇸🇬', name: 'Singapore' },
  { code: '+60', flag: '🇲🇾', name: 'Malaysia' },
  { code: '+1',  flag: '🇨🇦', name: 'Canada' },
];

const keyframes = `
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes logoFloat {
    0%,100% { transform: translateY(0px); }
    50%      { transform: translateY(-6px); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes ripple {
    0%   { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(19,136,8,0.55); }
    70%  { transform: scale(1);    box-shadow: 0 0 0 12px rgba(19,136,8,0); }
    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(19,136,8,0); }
  }
  @keyframes borderGlow {
    0%,100% { border-color: #138808; box-shadow: 0 0 0 3px rgba(19,136,8,0.15); }
    50%      { border-color: #FF9933; box-shadow: 0 0 0 3px rgba(255,153,51,0.2); }
  }
  @keyframes micPulse {
    0%   { box-shadow: 0 0 0 0 rgba(220,38,38,0.6); }
    70%  { box-shadow: 0 0 0 10px rgba(220,38,38,0); }
    100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); }
  }
`;

// ─── Deepgram voice recorder hook ───────────────────────────────────────────
const SILENCE_THRESHOLD = 10;   // RMS level below which we consider it silence
const SILENCE_DELAY_MS  = 2000; // stop after 3 s of continuous silence

const useDeepgramRecorder = (onTranscript) => {
  const mediaRecorderRef  = useRef(null);
  const chunksRef         = useRef([]);
  const audioContextRef   = useRef(null);
  const silenceTimerRef   = useRef(null);
  const animFrameRef      = useRef(null);
  const [recording, setRecording]       = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [silenceLeft, setSilenceLeft]   = useState(null); // countdown seconds shown to user

  // Internal stop — shared by manual stop and silence auto-stop
  const stopRecording = (mr, stream) => {
    // Cancel silence-detection loop
    if (animFrameRef.current)    cancelAnimationFrame(animFrameRef.current);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (audioContextRef.current) audioContextRef.current.close();
    animFrameRef.current    = null;
    silenceTimerRef.current = null;
    audioContextRef.current = null;
    setSilenceLeft(null);

    if (mr.state !== 'inactive') mr.stop();
    stream.getTracks().forEach((t) => t.stop());
    setRecording(false);
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];

      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        setTranscribing(true);
        try {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          const formData = new FormData();
          formData.append('audio', blob, 'recording.webm');
          const res  = await fetch(`${backendUrl}/api/transcribe`, {
            method: 'POST',
            body: formData,
            credentials: 'include',
          });
          const data = await res.json();
          if (data.text) onTranscript(data.text);
        } catch (err) {
          console.error('Transcription error:', err);
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();

      // ── Silence detection via Web Audio API ──────────────────────────────
      const audioCtx  = new (window.AudioContext || window.webkitAudioContext)();
      const analyser  = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      const source    = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);
      audioContextRef.current = audioCtx;

      const dataArray     = new Uint8Array(analyser.frequencyBinCount);
      let   silenceStart  = null; // timestamp when silence began

      const checkSilence = () => {
        analyser.getByteTimeDomainData(dataArray);
        // Compute RMS amplitude (0–128 range offset)
        const rms = Math.sqrt(
          dataArray.reduce((sum, v) => sum + (v - 128) ** 2, 0) / dataArray.length
        );

        if (rms < SILENCE_THRESHOLD) {
          if (!silenceStart) silenceStart = Date.now();
          const elapsed  = Date.now() - silenceStart;
          const remaining = Math.ceil((SILENCE_DELAY_MS - elapsed) / 1000);
          setSilenceLeft(remaining > 0 ? remaining : 0);
          if (elapsed >= SILENCE_DELAY_MS) {
            stopRecording(mr, stream);
            return; // exit loop
          }
        } else {
          silenceStart = null;
          setSilenceLeft(null);
        }
        animFrameRef.current = requestAnimationFrame(checkSilence);
      };

      animFrameRef.current = requestAnimationFrame(checkSilence);
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (err) {
      console.error('Mic access error:', err);
      alert('Microphone access denied. Please allow mic permissions and try again.');
    }
  };

  const stop = () => {
    if (mediaRecorderRef.current && recording) {
      if (animFrameRef.current)    cancelAnimationFrame(animFrameRef.current);
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
      animFrameRef.current    = null;
      silenceTimerRef.current = null;
      audioContextRef.current = null;
      setSilenceLeft(null);
      if (mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  return { recording, transcribing, silenceLeft, start, stop };
};

// ─── Mic Button ─────────────────────────────────────────────────────────────
const MicButton = ({ recording, transcribing, silenceLeft, onStart, onStop }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '0.5rem', flexShrink: 0 }}>
    <button
      type="button"
      onClick={recording ? onStop : onStart}
      disabled={transcribing}
      title={recording ? 'Stop recording' : 'Speak to fill this field'}
      style={{
        width: 36, height: 36,
        borderRadius: '50%',
        border: 'none',
        cursor: transcribing ? 'wait' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1rem',
        backgroundColor: recording ? '#DC2626' : transcribing ? '#f59e0b' : '#e8f5e9',
        color: recording ? '#fff' : transcribing ? '#fff' : '#138808',
        transition: 'background-color 0.2s ease',
        animation: recording ? 'micPulse 1.2s infinite' : 'none',
      }}
    >
      {transcribing ? '⏳' : recording ? '⏹' : '🎤'}
    </button>
    {recording && silenceLeft !== null && (
      <span style={{
        fontSize: '0.6rem', fontWeight: 700,
        color: '#DC2626', lineHeight: 1,
        marginTop: '2px',
        fontFamily: "'Helvetica Neue', sans-serif",
      }}>
        {silenceLeft}s
      </span>
    )}
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const PhoneAuth = ({ onComplete }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]);
  const [phoneNumber, setPhoneNumber]         = useState('');
  const [name, setName]                       = useState('');
  const [dropdownOpen, setDropdownOpen]       = useState(false);
  const [phoneFocused, setPhoneFocused]       = useState(false);
  const [nameFocused, setNameFocused]         = useState(false);
  const [isLoading, setIsLoading]             = useState(false);
  const [mounted, setMounted]                 = useState(false);
  const [logoUrl, setLogoUrl]                 = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    fetch(`${backendUrl}/api/signed-url/${encodeURIComponent('logo.jpeg')}`, {
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((data) => setLogoUrl(data.signedUrl))
      .catch((err) => console.error('Failed to load logo', err));
  }, [backendUrl]);

  // ── Name recorder: transcript goes straight into the name field ──
  const nameRecorder = useDeepgramRecorder((text) => {
    setName(text.trim());
  });

  // ── Phone recorder: extract only digits from transcript ──
  const phoneRecorder = useDeepgramRecorder((text) => {
    // Deepgram returns spoken numbers as words or digits.
    // Convert written words to digits then strip non-numeric chars.
    const wordMap = {
      zero:'0', one:'1', two:'2', three:'3', four:'4',
      five:'5', six:'6', seven:'7', eight:'8', nine:'9',
    };
    const normalised = text.toLowerCase().replace(
      /\b(zero|one|two|three|four|five|six|seven|eight|nine)\b/g,
      (m) => wordMap[m]
    );
    const digits = normalised.replace(/\D/g, '').slice(0, 10);
    if (digits) setPhoneNumber(digits);
  });

  const handleSubmit = async () => {
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length < 7 || name.trim().length < 2) return;

    setIsLoading(true);
    const fullPhone = `${selectedCountry.code}${digits}`;

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      await fetch(`${backendUrl}/api/notify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), phone: fullPhone }),
        credentials: 'include',
        signal: controller.signal,
      });
      clearTimeout(timeout);
    } catch (err) {
      console.error('Notification failed:', err.message);
    }

    setIsLoading(false);
    onComplete({ phone: fullPhone, name: name.trim() });
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSubmit(); };

  const formatDisplay = (val) => {
    const d = val.replace(/\D/g, '').slice(0, 10);
    if (d.length <= 5) return d;
    if (d.length <= 8) return `${d.slice(0,5)} ${d.slice(5)}`;
    return `${d.slice(0,5)} ${d.slice(5,8)} ${d.slice(8)}`;
  };

  const isValid = phoneNumber.replace(/\D/g, '').length >= 7 && name.trim().length >= 2;

  return (
    <>
      <style>{keyframes}</style>

      <div style={styles.page}>

        {/* Top tricolor bar */}
        <div style={styles.tricolorBar}>
          <div style={{ flex: 1, backgroundColor: '#FF9933' }} />
          <div style={{ flex: 1, backgroundColor: '#ffffff' }} />
          <div style={{ flex: 1, backgroundColor: '#138808' }} />
        </div>

        {/* Card */}
        <div style={{
          ...styles.card,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(32px)',
          transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
        }}>

          {/* Logo */}
          <div style={styles.logoWrap}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="5RsPhysio"
                style={styles.logo}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div style={styles.logoFallback}>5Rs</div>
            )}
          </div>

          <h1 style={styles.heading}>Welcome to 5RsPhysio</h1>
          <p style={styles.subheading}>Please enter your details to continue</p>

          <div style={styles.divider} />

          {/* ── Name Field ── */}
          <label style={styles.label}>Full Name</label>
          <div style={{
            ...styles.inputRow,
            ...(nameFocused ? styles.inputRowFocused : {}),
            ...(nameRecorder.recording ? styles.inputRowRecording : {}),
            marginBottom: '1.25rem',
          }}>
            <span style={styles.inputIcon}>👤</span>
            <div style={styles.inputDivider} />
            <input
              type="text"
              placeholder={nameRecorder.recording ? '🎙 Listening…' : nameRecorder.transcribing ? '⏳ Processing…' : 'Enter Your Name'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onFocus={() => setNameFocused(true)}
              onBlur={() => setNameFocused(false)}
              onKeyDown={handleKeyDown}
              style={styles.input}
              autoComplete="name"
              maxLength={60}
            />
            <MicButton
              recording={nameRecorder.recording}
              transcribing={nameRecorder.transcribing}
              silenceLeft={nameRecorder.silenceLeft}
              onStart={nameRecorder.start}
              onStop={nameRecorder.stop}
            />
          </div>

          {/* ── Phone Field ── */}
          <label style={styles.label}>Mobile Number</label>
          <div style={{
            ...styles.inputRow,
            ...(phoneFocused ? styles.inputRowFocused : {}),
            ...(phoneRecorder.recording ? styles.inputRowRecording : {}),
          }}>
            {/* Country Code Picker */}
            <div style={styles.countryPicker} onClick={() => setDropdownOpen((o) => !o)}>
              <span style={{ fontSize: '1.3rem' }}>{selectedCountry.flag}</span>
              <span style={styles.countryCode}>{selectedCountry.code}</span>
              <span style={styles.chevron}>{dropdownOpen ? '▲' : '▼'}</span>
            </div>

            <div style={styles.inputDivider} />

            <input
              type="tel"
              inputMode="numeric"
              placeholder={phoneRecorder.recording ? '🎙 Listening…' : phoneRecorder.transcribing ? '⏳ Processing…' : 'Enter your phone number'}
              value={formatDisplay(phoneNumber)}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
              onFocus={() => setPhoneFocused(true)}
              onBlur={() => setPhoneFocused(false)}
              onKeyDown={handleKeyDown}
              style={styles.input}
              autoComplete="tel"
            />
            <MicButton
              recording={phoneRecorder.recording}
              transcribing={phoneRecorder.transcribing}
              silenceLeft={phoneRecorder.silenceLeft}
              onStart={phoneRecorder.start}
              onStop={phoneRecorder.stop}
            />
          </div>

          {/* Dropdown */}
          {dropdownOpen && (
            <div style={styles.dropdown}>
              {COUNTRY_CODES.map((c, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.dropdownItem,
                    backgroundColor:
                      c.code === selectedCountry.code && c.name === selectedCountry.name
                        ? 'rgba(19,136,8,0.08)'
                        : 'transparent',
                  }}
                  onClick={() => { setSelectedCountry(c); setDropdownOpen(false); }}
                >
                  <span style={{ fontSize: '1.2rem' }}>{c.flag}</span>
                  <span style={styles.dropdownName}>{c.name}</span>
                  <span style={styles.dropdownCode}>{c.code}</span>
                </div>
              ))}
            </div>
          )}

          {/* CTA Button */}
          <button
            onClick={handleSubmit}
            disabled={!isValid || isLoading}
            style={{
              ...styles.btn,
              ...(!isValid || isLoading ? styles.btnDisabled : styles.btnActive),
            }}
          >
            {isLoading ? (
              <span style={styles.spinner} />
            ) : (
              <>Continue <span style={{ marginLeft: '0.5rem', fontSize: '1.1rem' }}>→</span></>
            )}
          </button>

          <p style={styles.footerNote}>
            🔒 Your details are only used to personalise your experience
          </p>
        </div>

        <p style={styles.tagline}>India's Healthy Ageing Platform</p>

        {/* Bottom tricolor bar */}
        <div style={{ ...styles.tricolorBar, position: 'fixed', bottom: 0 }}>
          <div style={{ flex: 1, backgroundColor: '#FF9933' }} />
          <div style={{ flex: 1, backgroundColor: '#ffffff' }} />
          <div style={{ flex: 1, backgroundColor: '#138808' }} />
        </div>
      </div>
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: '100vh',
    width: '100vw',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f7fa',
    backgroundImage: `
      radial-gradient(ellipse at 10% 20%, rgba(255,153,51,0.08) 0%, transparent 55%),
      radial-gradient(ellipse at 90% 80%, rgba(19,136,8,0.08) 0%, transparent 55%)
    `,
    padding: '3rem 1rem',
    boxSizing: 'border-box',
    fontFamily: "'Georgia', 'Times New Roman', serif",
  },
  tricolorBar: {
    position: 'fixed',
    top: 0, left: 0, right: 0,
    height: '5px',
    display: 'flex',
    zIndex: 100,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    padding: '2.5rem 2rem',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 8px 40px rgba(0,0,128,0.1), 0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,128,0.08)',
    position: 'relative',
    overflow: 'visible',
  },
  logoWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1.5rem',
    animation: 'logoFloat 4s ease-in-out infinite',
  },
  logo: {
    width: '120px',
    height: 'auto',
    borderRadius: '16px',
    boxShadow: '0 4px 16px rgba(0,0,128,0.12)',
  },
  logoFallback: {
    width: '100px', height: '100px',
    borderRadius: '50%',
    backgroundColor: '#000080',
    color: '#FF9933',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.8rem', fontWeight: 700, letterSpacing: '1px',
    boxShadow: '0 4px 16px rgba(0,0,128,0.2)',
  },
  heading: {
    textAlign: 'center',
    fontSize: '1.6rem', fontWeight: 700,
    color: '#000080',
    margin: '0 0 0.5rem',
    fontFamily: "'Georgia', serif",
    letterSpacing: '-0.3px',
  },
  subheading: {
    textAlign: 'center',
    fontSize: '0.95rem', color: '#666',
    margin: '0 0 1.5rem', lineHeight: 1.5,
    fontFamily: "'Helvetica Neue', sans-serif",
  },
  divider: {
    height: '2px',
    background: 'linear-gradient(90deg, #FF9933, #ffffff 30%, #138808 60%, #ffffff 75%, #000080)',
    borderRadius: '2px',
    marginBottom: '1.75rem',
    opacity: 0.6,
  },
  label: {
    display: 'block',
    fontSize: '0.82rem', fontWeight: 600,
    color: '#000080',
    marginBottom: '0.5rem',
    textTransform: 'uppercase', letterSpacing: '0.8px',
    fontFamily: "'Helvetica Neue', sans-serif",
  },
  inputIcon: {
    padding: '0.75rem 0.85rem',
    fontSize: '1.1rem', flexShrink: 0,
  },
  inputRow: {
    display: 'flex', alignItems: 'center',
    border: '2px solid #d0d5dd',
    borderRadius: '12px',
    overflow: 'visible',
    backgroundColor: '#fff',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    position: 'relative',
  },
  inputRowFocused: {
    borderColor: '#138808',
    boxShadow: '0 0 0 3px rgba(19,136,8,0.12)',
  },
  inputRowRecording: {
    borderColor: '#DC2626',
    boxShadow: '0 0 0 3px rgba(220,38,38,0.15)',
  },
  countryPicker: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '0.75rem 0.85rem',
    cursor: 'pointer', userSelect: 'none', flexShrink: 0,
    borderRadius: '10px 0 0 10px',
    transition: 'background-color 0.15s',
  },
  countryCode: {
    fontSize: '0.95rem', fontWeight: 600, color: '#000080',
    fontFamily: "'Helvetica Neue', sans-serif",
  },
  chevron: { fontSize: '0.6rem', color: '#888' },
  inputDivider: {
    width: '1px', height: '28px',
    backgroundColor: '#e0e0e0', flexShrink: 0,
  },
  input: {
    flex: 1, border: 'none', outline: 'none',
    fontSize: '1.15rem',
    padding: '0.75rem 1rem',
    fontFamily: "'Helvetica Neue', sans-serif",
    letterSpacing: '0.5px', color: '#1a1a2e',
    backgroundColor: 'transparent', minWidth: 0,
  },
  dropdown: {
    marginTop: '4px',
    backgroundColor: '#fff',
    border: '1.5px solid #e0e0e0',
    borderRadius: '12px', overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    zIndex: 200, position: 'relative',
  },
  dropdownItem: {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    padding: '0.65rem 1rem',
    cursor: 'pointer', transition: 'background-color 0.15s',
    fontFamily: "'Helvetica Neue', sans-serif",
  },
  dropdownName: { flex: 1, fontSize: '0.9rem', color: '#333' },
  dropdownCode: { fontSize: '0.88rem', color: '#888', fontWeight: 600 },
  btn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', padding: '0.9rem',
    fontSize: '1.05rem', fontWeight: 700,
    borderRadius: '12px', border: 'none', cursor: 'pointer',
    marginTop: '1.5rem',
    fontFamily: "'Helvetica Neue', sans-serif",
    letterSpacing: '0.3px', transition: 'all 0.2s ease',
  },
  btnActive: {
    backgroundColor: '#138808', color: '#ffffff',
    boxShadow: '0 4px 16px rgba(19,136,8,0.3)',
    animation: 'ripple 2s infinite',
  },
  btnDisabled: {
    backgroundColor: '#e9ecef', color: '#adb5bd',
    cursor: 'not-allowed', boxShadow: 'none',
  },
  spinner: {
    display: 'inline-block', width: '20px', height: '20px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#ffffff', borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  footerNote: {
    textAlign: 'center', fontSize: '0.78rem', color: '#aaa',
    marginTop: '1rem', marginBottom: 0, lineHeight: 1.5,
    fontFamily: "'Helvetica Neue', sans-serif",
  },
  tagline: {
    marginTop: '1.75rem', fontSize: '0.85rem', color: '#888',
    fontFamily: "'Georgia', serif", fontStyle: 'italic', letterSpacing: '0.3px',
  },
};

// Spinner keyframe + hover effects
const addPhoneAuthStyles = () => {
  const id = 'phone-auth-styles';
  if (document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    [data-phone-country]:hover { background-color: rgba(19,136,8,0.06) !important; }
  `;
  document.head.appendChild(s);
};
if (typeof document !== 'undefined') addPhoneAuthStyles();

export default PhoneAuth;